import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';

import { Table } from '../components/Table';

export interface UseCheckboxColumnOptions<TData> {
  data: TData[];
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: TData) => string | null | undefined;
  getItemId?: (data: TData) => string;
  isSelectionDisabled?: (data: TData) => string | undefined;
}

export function useCheckboxColumn<TData>({
  data,
  alwaysShowCheckbox,
  showSelectAllCheckbox = true,
  getGroupId,
  getItemId,
  isSelectionDisabled,
}: UseCheckboxColumnOptions<TData>) {
  const { selectionMode, getSelection } = useMultiSelectionContext();
  const selections = getSelection();

  // Determine if checkbox should be shown
  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      selectionMode?.mode === MultiSelectScope.SingleGroup ||
      selections.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      selections.isSingleGroupMultiSelect,
      selectionMode?.mode,
    ]
  );

  // Create checkbox column definition
  const checkboxColumn: ColumnDef<TData> = useMemo(
    () => ({
      id: 'checkbox',
      header: () => {
        return showSelectAllCheckbox ? (
          <Table.SelectAllCheckbox
            showSelectAllCheckbox={showSelectAllCheckbox}
            data={data}
            getGroupId={getGroupId}
            getItemId={getItemId}
          />
        ) : null;
      },
      cell: ({ row, table }) => (
        <Table.RowCheckbox
          row={row}
          table={table}
          getGroupId={getGroupId}
          getItemId={getItemId}
          isSelectionDisabled={isSelectionDisabled}
        />
      ),
      size: 26,
      enableSorting: false,
      enableResizing: false,
      enableColumnFilter: false,
    }),
    [data, getGroupId, getItemId, isSelectionDisabled, showSelectAllCheckbox]
  );

  return {
    showCheckbox,
    checkboxColumn,
  };
}
